import { useEffect, useState, type ReactNode } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { DevAuthPanel } from "./DevAuthPanel";
import { useAuthStore } from "../stores/auth";
import { logout as logoutRequest, resendVerification } from "../lib/api/auth";
import { apiErrorMessage } from "../lib/api/client";
import { getOAuthCapabilities } from "../lib/api/oauth";
import agentNativeMarkSmall from "../assets/agent-native-mark-small-white.svg";

// Routes that render their own full-bleed shell (AuthLayout) and must
// NOT get AppLayout's nav chrome on top -- a logged-out visitor on
// /login has no use for a nav bar linking to pages they can't use yet.
// This is a router-structure limitation workaround: everything hangs
// off one rootRoute (router.tsx), so there's no layout-route split to
// exempt these paths from AppLayout the "normal" TanStack Router way
// without a larger restructure.
const BARE_ROUTES = new Set(["/login", "/register", "/verify-email", "/forgot-password", "/reset-password"]);

// Grouped sidebar sections -- matching the convention LedgerCat
// (entity view, ~36 destinations grouped by Ledger/Sales/Purchases/...)
// and Project Panda (org view: WORK/INBOX/ORGANIZATION) both use once
// you're inside an organization/entity, rather than a flat single-row
// nav list. This console route tree is smaller, so three groups is
// enough: what THIS org is/does, how people get in, and the platform-
// admin-only group kept visually separate (reusing the existing
// brand-primary-colored treatment for it).
const navGroups = [
  {
    label: "Organization",
    items: [
      { to: "/", label: "Dashboard" },
      { to: "/settings/organization", label: "Settings" },
      { to: "/settings/members", label: "Members" },
      { to: "/settings/billing", label: "Billing" },
    ],
  },
  {
    label: "Licensing",
    items: [
      { to: "/settings/licenses", label: "My Licenses" },
      { to: "/marketplace", label: "Marketplace" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/settings/sessions", label: "Sessions" },
      { to: "/settings/tokens", label: "API Tokens" },
      { to: "/settings/connected-apps", label: "Connected apps" },
    ],
  },
] as const;

// Shown only to people the API will let in (organization owners for agents, owners and
// admins for the audit). The server re-checks every call; this just avoids dead links.
const oauthNavItems = [
  { to: "/settings/agents", label: "Agents", cap: "manage_agents" },
  { to: "/settings/oauth-audit", label: "OAuth audit", cap: "view_audit" },
] as const;

function OAuthNavItems() {
  const user = useAuthStore((s) => s.user);
  const caps = useQuery({ queryKey: ["oauth-capabilities"], queryFn: getOAuthCapabilities, enabled: !!user, retry: false });
  const items = oauthNavItems.filter((i) => caps.data?.[i.cap]);
  return (
    <>
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="block border-l-2 border-transparent px-4 py-1.5 font-sans text-[13px] text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-200)] hover:text-[var(--color-neutral-900)]"
          activeProps={{
            className:
              "block border-l-2 border-[var(--color-brand-primary)] bg-[var(--color-neutral-200)] px-4 py-1.5 font-sans text-[13px] font-medium text-[var(--color-neutral-900)]",
          }}
        >
          {item.label}
        </Link>
      ))}
    </>
  );
}

const adminNavItems = [
  { to: "/admin/organizations", label: "Organizations" },
  { to: "/admin/licenses/issue", label: "Issue License" },
  { to: "/admin/oauth-resources", label: "OAuth Resources" },
  { to: "/admin/oauth-apps", label: "OAuth Apps" },
  { to: "/admin/oauth-clients", label: "Dynamic Clients" },
];

// Only rendered for a platform admin (see migration
// 0015_add_platform_admin, authsvc.RequirePlatformAdmin) -- these
// routes call CreateApp/IssueLicense/etc. under the hood, which the
// backend now rejects for anyone else with PermissionDenied; hiding the
// links too means a non-admin never sees an action they'd just get
// rejected for. Renders nothing while initialized is false (same
// bootstrap-timing reasoning as AccountControls) so a real admin
// doesn't see these links disappear-then-reappear on every load.
function AdminNavItems() {
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);

  if (!initialized || !user?.isPlatformAdmin) {
    return null;
  }

  return (
    <>
      {adminNavItems.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="text-[var(--color-brand-primary)] hover:text-[var(--color-neutral-900)]"
          activeProps={{ className: "font-medium text-[var(--color-neutral-900)]" }}
        >
          {item.label}
        </Link>
      ))}
      <span className="h-4 w-px bg-[var(--color-neutral-300)]" />
    </>
  );
}

function AccountControls() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const clear = useAuthStore((s) => s.clear);

  async function handleLogout() {
    try {
      await logoutRequest();
    } finally {
      // Clear local state regardless of whether the network call
      // succeeded -- the user clicked "log out," so the UI should reflect
      // that immediately even if the request failed (cookies are also
      // HttpOnly, so there's nothing else client-side to clean up).
      clear();
      navigate({ to: "/login" });
    }
  }

  // Render nothing while main.tsx's SessionBootstrap is still checking
  // the refresh-token cookie -- otherwise an actually-logged-in user
  // would see a "Sign in" flash on every fresh page load, for the brief
  // window before that check resolves.
  if (!initialized) {
    return null;
  }

  if (!user) {
    return (
      <Link
        to="/login"
        className="font-sans text-[13px] text-[var(--color-neutral-600)] hover:text-[var(--color-neutral-900)]"
      >
        Sign in
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-3 font-sans text-[13px]">
      <span className="text-[var(--color-neutral-600)]">{user.email}</span>
      <button
        type="button"
        onClick={handleLogout}
        className="text-[var(--color-neutral-600)] hover:text-[var(--color-neutral-900)]"
      >
        Sign out
      </button>
    </div>
  );
}

// Structural shell shared across the ecosystem (LedgerCat, Project
// Panda): a compact ~52px topbar (small square logo mark tight against
// the wordmark, hairline border, sans-serif text) PLUS a left sidebar,
// grouped by section with small-caps labels, once inside an
// organization -- both reference apps are topbar-only on their org/
// entity PICKER screen and grow a sidebar once you're actually working
// inside one, which this console's Dashboard already is (it shows
// "Acting as organization ..."). Colors/fonts stay Agent Native's own
// (platinum/slate palette, Sora for UI text -- Playfair Display stays
// reserved for actual display headings, not nav chrome).
// Shown to a signed-in user whose email is not verified yet. Apps that sign
// people in with Agent Native (e.g. GRC Cat) refuse an unverified address, so
// this is the one thing standing between a new account and those apps.
// Mirrors the server's resend cooldown (services/auth/email_verify.go,
// verificationCooldown). The server is the authority and answers 429 with
// retry_after_seconds if this is ever out of step; this only keeps the button
// from inviting a click that would be refused.
const RESEND_COOLDOWN_SECONDS = 60;

function VerifyEmailBanner() {
  const user = useAuthStore((s) => s.user);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Count the cooldown down once a second, stopping at zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  if (!user || user.emailVerified) return null;

  async function resend() {
    setBusy(true);
    try {
      const r = await resendVerification();
      setNote(r.message || "Verification email sent. Check your inbox, and your spam folder.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        const body = err.response.data as { retry_after_seconds?: number; message?: string } | undefined;
        setCooldown(Math.max(1, Math.ceil(body?.retry_after_seconds ?? RESEND_COOLDOWN_SECONDS)));
        setNote("An email was just sent. Check your inbox, and your spam folder.");
      } else {
        setNote(apiErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const waiting = cooldown > 0;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-neutral-300)] bg-[var(--color-neutral-200)] px-5 py-2 font-sans text-[13px]">
      <span>
        Verify your email address ({user.email}) to use Agent Native to sign in to your apps.
      </span>
      <button
        type="button"
        onClick={resend}
        disabled={busy || waiting}
        className="font-medium underline disabled:no-underline disabled:opacity-60"
      >
        {busy ? "Sending..." : waiting ? `Resend in ${cooldown}s` : "Resend verification email"}
      </button>
      {note && <span className="text-[var(--color-neutral-700)]">{note}</span>}
    </div>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (BARE_ROUTES.has(pathname)) {
    return <>{children}</>;
  }
  return (
    <div className="min-h-screen bg-[var(--color-neutral-100)] text-[var(--color-neutral-900)]">
      <header className="sticky top-0 z-20 flex h-[52px] items-center gap-3 border-b border-[var(--color-neutral-300)] bg-[var(--color-neutral-200)] px-5">
        <Link
          to="/"
          className="flex items-center gap-2 font-sans text-sm font-semibold text-[var(--color-neutral-900)] hover:text-[var(--color-neutral-900)]"
        >
          <img src={agentNativeMarkSmall} alt="" className="h-5 w-5" />
          Agent Native
        </Link>
        <div className="ml-auto flex items-center gap-4 font-sans text-[13px]">
          <AdminNavItems />
          <AccountControls />
        </div>
      </header>
      <VerifyEmailBanner />
      <DevAuthPanel />
      <div className="flex">
        <aside className="sticky top-[52px] flex h-[calc(100vh-52px)] w-[200px] flex-none flex-col overflow-y-auto border-r border-[var(--color-neutral-300)] bg-[var(--color-neutral-100)] py-4">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <div className="mb-1 px-4 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-neutral-600)]">
                {group.label}
              </div>
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="block border-l-2 border-transparent px-4 py-1.5 font-sans text-[13px] text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-200)] hover:text-[var(--color-neutral-900)]"
                  activeProps={{
                    className:
                      "block border-l-2 border-[var(--color-brand-primary)] bg-[var(--color-neutral-200)] px-4 py-1.5 font-sans text-[13px] font-medium text-[var(--color-neutral-900)]",
                  }}
                >
                  {item.label}
                </Link>
              ))}
              {group.label === "Account" && <OAuthNavItems />}
            </div>
          ))}
        </aside>
        <main className="min-w-0 flex-1 px-6 py-6">{children}</main>
      </div>
    </div>
  );
}
